import * as React from 'react';
import {createRoot, Root} from 'react-dom/client';
import {Component, RefObject} from 'react';
import {Flex} from '@priority-software/priority-style-react';
import {getServerTexts} from './texts';
import {ModalTitle} from './components/ModalTitle';
import {VerificationContent} from './components/VerificationContent';
import {ConfirmationContent} from './components/ConfirmationContent';
import {EmailChangedConfirmation} from './components/EmailChangedConfirmation';
import {validateEmail} from './utils';
import {keyCodes} from '../../constants';
import {sendEmailValidation} from '../../_shared/GWTBridge/invoke/genPage';
import {isEmailValidationNeeded, getVerifivationEmail} from '../../_shared/GWTBridge/invoke/common';
import styles from './EmailVerification.module.scss';

const modalmailVerificationRef: React.RefObject<EmailVerification> = React.createRef();

let rootEmail;

export async function renderEmailVerification(root: Root) {
    await getServerTexts();
    root.render(<EmailVerification ref={modalmailVerificationRef} />);

    (window as any).modalEmailVerificationRef = modalmailVerificationRef;
}

type EmailVerificationState = {
    validationStep: number;
    email: string;
    isValid: boolean;
    isClicked: boolean;
    emailInSystem: string;
    isOpen: boolean;
};

class EmailVerification extends Component<any, EmailVerificationState> {
    inputRef: RefObject<HTMLInputElement>;
    constructor(props) {
        super(props);
        this.state = {
            validationStep: 0,
            email: '',
            isValid: false,
            isClicked: false,
            emailInSystem: '',
            isOpen: false
        };

        this.inputRef = React.createRef();
    }

    componentDidMount() {
        this.inputRef.current && this.inputRef.current.focus();

        if (isEmailValidationNeeded()) {
            this.handleModalStep(1, getVerifivationEmail().trim());
        }
    }

    handleModalStep = (step: number, email?) => {
        const isValid = validateEmail(this.state.email) || validateEmail(email ? email : '');
        this.setState(
            {
                validationStep: step,
                email: email ? email : this.state.email,
                emailInSystem: email ? email : this.state.email,
                isValid: isValid,
                isClicked: false
            },
            () => {
                if (step > 0) {
                    if (step === 1) {
                        this.inputRef.current && this.inputRef.current.focus();
                    }

                    window.addEventListener('keydown', this.handleKeyDown, true);
                } else {
                    window.removeEventListener('keydown', this.handleKeyDown, true);
                    if (!rootEmail)
                        rootEmail = createRoot(document.getElementById('react-email-verification'));

                    rootEmail.unmount();
                }
            }
        );
    };

    handleKeyDown = e => {
        if (e.keyCode === keyCodes.ENTER) {
            e.stopPropagation();
            const isValid = validateEmail(this.state.email);
            if (this.state.validationStep === 1 && isValid) {
                this.handleSubmitEmail();
            } else if (this.state.validationStep === 2) {
                this.handleModalStep(0);
            }
        }
    };

    handleSubmitEmail = () => {
        if (this.state.email !== this.state.emailInSystem) {
            this.setState({isOpen: true});
        } else {
            this.submitEmail();
        }
    };

    handleConfirm = () => {
        this.setState({isOpen: false});
        this.submitEmail();
    };

    setEmail = e => {
        const isValid = validateEmail(e.target.value);
        this.setState({email: e.target.value, isValid: isValid});
    };

    submitEmail = () => {
        sendEmailValidation(this.state.email);
        this.setState({isClicked: true}, () => {
            setTimeout(() => {
                this.setState({isClicked: false});
            }, 2000);
        });
    };

    renderEmailVerificationModal = () => {
        const {validationStep, email} = this.state;
        const disabled = !this.state.isValid || this.state.isClicked;
        const imgUrl = `${window['baseUrl']}/images/evHeaderBg.png`;
        return (
            <>
                <section>
                    <div className={styles.mask}>
                        <div className={styles.centerEv}>
                            <div
                                style={{backgroundImage: `url(${imgUrl})`}}
                                className={styles.modalHero}
                            />
                            <div className={styles.contentWrapper}>
                                <Flex direction="column" componentClasses={styles.content}>
                                    <ModalTitle step={validationStep} />
                                    {validationStep === 1 && (
                                        <VerificationContent
                                            email={email}
                                            setEmail={this.setEmail}
                                            isBtnDisabled={disabled}
                                            submitEmail={this.handleSubmitEmail}
                                        />
                                    )}
                                    {validationStep === 2 && (
                                        <ConfirmationContent
                                            handleModalStep={this.handleModalStep}
                                        />
                                    )}
                                </Flex>
                            </div>
                        </div>
                    </div>
                </section>
                {this.state.isOpen && (
                    <EmailChangedConfirmation
                        isOpen={this.state.isOpen}
                        onCancel={() => this.setState({isOpen: false})}
                        onConfirm={this.handleConfirm}
                    />
                )}
            </>
        );
    };

    render() {
        const {validationStep} = this.state;
        if (!validationStep) {
            return null;
        } else if (validationStep === 1 || validationStep === 2) {
            return this.renderEmailVerificationModal();
        } else {
            return null;
        }
    }
}
